/**
 * The Commissioner's score correction review on web (ISSUE-1325, SUR-R62a).
 *
 * The league sometimes corrects a score after a contest was confirmed. The
 * server never quietly re-grades a confirmed contest; it records the
 * correction and asks. This card is where the Commissioner answers.
 *
 * `PoolCorrectionReviews` asks `/contest/reopen-preview-get` about each
 * confirmed contest of the season. A contest with nothing pending answers
 * NOT_FOUND and draws nothing. A contest with a correction draws one card:
 * what changed, who it moves, and whether the winners change. Then two
 * answers, each confirmed first:
 *
 *  - Apply the correction: `/contest/reopen`, then `/contest/reopen-resolve`
 *    with `accept`. When the server will not apply it on its own (it would
 *    change a player who is out), the card says so and offers to apply the
 *    exact changes it just showed, as `editAndApply`.
 *  - Keep the results as they are: `/contest/reopen`, then resolve `reject`.
 *
 * The preview's digest goes with every resolve, so the Commissioner can only
 * ever act on the correction they were shown.
 */
import {
  useContestList,
  useContestReopen,
  useContestReopenPreviewGet,
  useContestReopenResolve,
  useStandingsGet,
} from "@sportsmet/contest-sdk/private";
import { Button, confirmDestructive, InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import {
  affectedEntryLine,
  buildEditAndApplyOverride,
  CORRECTION_APPLY_ANYWAY_CONFIRM_BODY,
  CORRECTION_APPLY_ANYWAY_CONFIRM_TITLE,
  CORRECTION_APPLY_ANYWAY_LABEL,
  CORRECTION_APPLY_CONFIRM_BODY,
  CORRECTION_APPLY_CONFIRM_TITLE,
  CORRECTION_APPLY_LABEL,
  CORRECTION_APPLYING_LABEL,
  CORRECTION_CHANGES_TITLE,
  CORRECTION_CONFIRM_CANCEL_LABEL,
  CORRECTION_KEEP_CONFIRM_BODY,
  CORRECTION_KEEP_CONFIRM_LABEL,
  CORRECTION_KEEP_CONFIRM_TITLE,
  CORRECTION_KEEP_LABEL,
  CORRECTION_KEEPING_LABEL,
  CORRECTION_MOVED_NOTE,
  CORRECTION_NEEDS_EDIT_NOTE,
  CORRECTION_NO_ENTRY_CHANGES,
  CORRECTION_TITLE,
  correctionIntroLine,
  isAlreadyReopened,
  resolveRefusalTreatment,
  type typeCorrectionPreview,
  winnersLine,
} from "./correctionReviewModel";

type typeEnvelopeError = {
  code?: string;
  details?: Record<string, unknown>;
} | null;

function readError(result: unknown): typeEnvelopeError {
  if (typeof result !== "object" || result === null) return null;
  const { error } = result as { error?: typeEnvelopeError };
  return error ?? null;
}

/** One card per confirmed contest that has a correction waiting. */
export const PoolCorrectionReviews = ({
  poolId,
}: {
  poolId: string;
}): ReactNode => {
  const list = useContestList({ poolId });
  const confirmed = (list.data?.data?.items ?? []).filter(
    (item) => item.status === "confirmed",
  );
  if (confirmed.length === 0) return null;
  return (
    <>
      {confirmed.map((contest) => (
        <ContestCorrectionReview
          key={contest.contestId}
          poolId={poolId}
          contestId={contest.contestId}
          contestName={contest.name}
        />
      ))}
    </>
  );
};

type typeAction = "apply" | "keep" | "applyAnyway";

export const ContestCorrectionReview = ({
  poolId,
  contestId,
  contestName,
}: {
  poolId: string;
  contestId: string;
  contestName: string;
}): ReactNode => {
  const preview = useContestReopenPreviewGet(
    { poolId, contestId },
    { staleTime: 0 },
  );
  const data = preview.data?.data ?? null;
  const standings = useStandingsGet(
    { poolId, contestId },
    { enabled: data !== null },
  );
  const reopen = useContestReopen();
  const resolve = useContestReopenResolve();
  const [pending, setPending] = useState<typeAction | null>(null);
  const [note, setNote] = useState<"needsEdit" | "moved" | null>(null);

  // NOT_FOUND is the ordinary "nothing to review" answer, and any other
  // refusal of this optional read is not worth a modal over the Game tab.
  if (data === null || extractErrorCode(preview.data) !== null) return null;

  const view: typeCorrectionPreview = data;
  const namesByEntryId = new Map<string, string>();
  for (const row of standings.data?.data?.entries ?? []) {
    namesByEntryId.set(row.entryId, row.displayName);
  }
  for (const entry of view.affectedEntries) {
    namesByEntryId.set(entry.entryId, entry.displayName);
  }

  async function run(action: typeAction): Promise<void> {
    if (pending !== null) return;
    const ask =
      action === "keep"
        ? {
            title: CORRECTION_KEEP_CONFIRM_TITLE,
            body: CORRECTION_KEEP_CONFIRM_BODY,
            confirmLabel: CORRECTION_KEEP_CONFIRM_LABEL,
          }
        : action === "apply"
          ? {
              title: CORRECTION_APPLY_CONFIRM_TITLE,
              body: CORRECTION_APPLY_CONFIRM_BODY,
              confirmLabel: CORRECTION_APPLY_LABEL,
            }
          : {
              title: CORRECTION_APPLY_ANYWAY_CONFIRM_TITLE,
              body: CORRECTION_APPLY_ANYWAY_CONFIRM_BODY,
              confirmLabel: CORRECTION_APPLY_ANYWAY_LABEL,
            };
    const isConfirmed = await confirmDestructive({
      ...ask,
      cancelLabel: CORRECTION_CONFIRM_CANCEL_LABEL,
    });
    if (!isConfirmed) return;

    setPending(action);
    try {
      const opened = await reopen.mutateAsync({ poolId, contestId });
      const openError = readError(opened);
      if (openError !== null && !isAlreadyReopened(openError)) {
        showApiError(opened);
        return;
      }
      const result = await resolve.mutateAsync(
        action === "applyAnyway"
          ? {
              poolId,
              contestId,
              decision: "editAndApply",
              previewResultsDigest: view.previewResultsDigest,
              override: buildEditAndApplyOverride(view),
            }
          : {
              poolId,
              contestId,
              decision: action === "keep" ? "reject" : "accept",
              previewResultsDigest: view.previewResultsDigest,
            },
      );
      const resolveError = readError(result);
      if (resolveError !== null) {
        const treatment = resolveRefusalTreatment(resolveError);
        if (treatment === "modal") {
          showApiError(result);
          return;
        }
        setNote(treatment);
        if (treatment === "moved") void preview.refetch();
        return;
      }
      setNote(null);
      void preview.refetch();
      void standings.refetch();
    } finally {
      setPending(null);
    }
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-warning p-4"
      data-testid={`correction-review-${contestId}`}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-body-strong text-ink">{CORRECTION_TITLE}</h3>
        <p className="text-caption text-ink-secondary">{contestName}</p>
      </div>
      <p className="text-body text-ink">
        {correctionIntroLine({
          roundNumber: view.roundNumber,
          refusalReason: view.refusalReason,
        })}
      </p>
      <div className="flex flex-col gap-2">
        <h4 className="text-body text-ink">{CORRECTION_CHANGES_TITLE}</h4>
        {view.affectedEntries.length === 0 ? (
          <p className="text-caption text-ink-secondary">
            {CORRECTION_NO_ENTRY_CHANGES}
          </p>
        ) : (
          <ul
            className="flex flex-col gap-1"
            data-testid={`correction-review-entries-${contestId}`}
          >
            {view.affectedEntries.map((entry) => (
              <li key={entry.entryId} className="text-caption text-ink">
                {affectedEntryLine(entry)}
              </li>
            ))}
          </ul>
        )}
        <p
          className="text-caption text-ink"
          data-testid={`correction-review-winners-${contestId}`}
        >
          {winnersLine({
            current: view.currentWinnerEntryIds,
            proposed: view.proposedWinnerEntryIds,
            namesByEntryId,
          })}
        </p>
      </div>
      {note === null ? null : (
        <div data-testid={`correction-review-note-${contestId}`}>
          <InlineBanner
            variant="warning"
            message={
              note === "needsEdit"
                ? CORRECTION_NEEDS_EDIT_NOTE
                : CORRECTION_MOVED_NOTE
            }
          />
        </div>
      )}
      <div className="flex flex-col gap-2 md:flex-row md:justify-end">
        <Button
          variant="secondary"
          loading={pending === "keep"}
          loadingLabel={CORRECTION_KEEPING_LABEL}
          data-testid={`correction-review-keep-${contestId}`}
          onClick={() => {
            void run("keep");
          }}
        >
          {CORRECTION_KEEP_LABEL}
        </Button>
        {note === "needsEdit" ? (
          <Button
            variant="primary"
            loading={pending === "applyAnyway"}
            loadingLabel={CORRECTION_APPLYING_LABEL}
            data-testid={`correction-review-apply-anyway-${contestId}`}
            onClick={() => {
              void run("applyAnyway");
            }}
          >
            {CORRECTION_APPLY_ANYWAY_LABEL}
          </Button>
        ) : (
          <Button
            variant="primary"
            loading={pending === "apply"}
            loadingLabel={CORRECTION_APPLYING_LABEL}
            data-testid={`correction-review-apply-${contestId}`}
            onClick={() => {
              void run("apply");
            }}
          >
            {CORRECTION_APPLY_LABEL}
          </Button>
        )}
      </div>
    </section>
  );
};
